import React, { useState, useEffect } from "react";
import "./KeyboardTest.css";

const KeyboardTest = () => {
  const [activeKeys, setActiveKeys] = useState(new Set());
  const [testedKeys, setTestedKeys] = useState(new Set());
  const inputRef = React.useRef(null);

  useEffect(() => {
    // Autofocus the hidden input
    if (inputRef.current) {
      inputRef.current.focus();
    }

    const handleKeyDown = (e) => {
      // Prevent default browser behavior for all keys to block shortcuts
      e.preventDefault();
      e.stopPropagation();
      
      let code = e.code;
      
      // Map Numpad numbers to main Digit row if they exist there
      if (code.startsWith("Numpad") && !isNaN(code.replace("Numpad", ""))) {
        const num = code.replace("Numpad", "");
        code = `Digit${num}`;
      }

      setActiveKeys((prev) => new Set(prev).add(code));
      setTestedKeys((prev) => new Set(prev).add(code));
    };

    const handleKeyUp = (e) => {
      e.preventDefault();
      e.stopPropagation();

      let code = e.code;
      
      // Map Numpad numbers to main Digit row
      if (code.startsWith("Numpad") && !isNaN(code.replace("Numpad", ""))) {
        const num = code.replace("Numpad", "");
        code = `Digit${num}`;
      }

      setActiveKeys((prev) => {
        const next = new Set(prev);
        next.delete(code);
        return next;
      });
    };

    window.addEventListener("keydown", handleKeyDown, true);
    window.addEventListener("keyup", handleKeyUp, true);

    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
      window.removeEventListener("keyup", handleKeyUp, true);
    };
  }, []);

  const resetTest = () => {
    setTestedKeys(new Set());
    setActiveKeys(new Set());
    if (inputRef.current) inputRef.current.focus();
  };

  const handleContainerClick = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const getKeyClass = (code) => {
    let className = "key";
    if (activeKeys.has(code)) {
      className += " active";
    } else if (testedKeys.has(code)) {
      className += " tested";
    }
    return className;
  };

  // Keyboard Layout Definition
  const rows = [
    [
      { code: "Escape", label: "Esc", width: "key-md" },
      { code: "F1", label: "F1" },
      { code: "F2", label: "F2" },
      { code: "F3", label: "F3" },
      { code: "F4", label: "F4" },
      { code: "F5", label: "F5" },
      { code: "F6", label: "F6" },
      { code: "F7", label: "F7" },
      { code: "F8", label: "F8" },
      { code: "F9", label: "F9" },
      { code: "F10", label: "F10" },
      { code: "F11", label: "F11" },
      { code: "F12", label: "F12" },
    ],
    [
      { code: "Backquote", label: "`" },
      { code: "Digit1", label: "1" },
      { code: "Digit2", label: "2" },
      { code: "Digit3", label: "3" },
      { code: "Digit4", label: "4" },
      { code: "Digit5", label: "5" },
      { code: "Digit6", label: "6" },
      { code: "Digit7", label: "7" },
      { code: "Digit8", label: "8" },
      { code: "Digit9", label: "9" },
      { code: "Digit0", label: "0" },
      { code: "Minus", label: "-" },
      { code: "Equal", label: "=" },
      { code: "Backspace", label: "Backspace", width: "key-xl" },
    ],
    [
      { code: "Tab", label: "Tab", width: "key-lg" },
      { code: "KeyQ", label: "Q" },
      { code: "KeyW", label: "W" },
      { code: "KeyE", label: "E" },
      { code: "KeyR", label: "R" },
      { code: "KeyT", label: "T" },
      { code: "KeyY", label: "Y" },
      { code: "KeyU", label: "U" },
      { code: "KeyI", label: "I" },
      { code: "KeyO", label: "O" },
      { code: "KeyP", label: "P" },
      { code: "BracketLeft", label: "[" },
      { code: "BracketRight", label: "]" },
      { code: "Backslash", label: "\\", width: "key-lg" },
    ],
    [
      { code: "CapsLock", label: "Caps Lock", width: "key-xl" },
      { code: "KeyA", label: "A" },
      { code: "KeyS", label: "S" },
      { code: "KeyD", label: "D" },
      { code: "KeyF", label: "F" },
      { code: "KeyG", label: "G" },
      { code: "KeyH", label: "H" },
      { code: "KeyJ", label: "J" },
      { code: "KeyK", label: "K" },
      { code: "KeyL", label: "L" },
      { code: "Semicolon", label: ";" },
      { code: "Quote", label: "'" },
      { code: "Enter", label: "Enter", width: "key-xl" },
    ],
    [
      { code: "ShiftLeft", label: "Shift", width: "key-xl" },
      { code: "KeyZ", label: "Z" },
      { code: "KeyX", label: "X" },
      { code: "KeyC", label: "C" },
      { code: "KeyV", label: "V" },
      { code: "KeyB", label: "B" },
      { code: "KeyN", label: "N" },
      { code: "KeyM", label: "M" },
      { code: "Comma", label: "," },
      { code: "Period", label: "." },
      { code: "Slash", label: "/" },
      { code: "ShiftRight", label: "Shift", width: "key-xl" },
    ],
    [
      { code: "ControlLeft", label: "Ctrl", width: "key-md" },
      { code: "MetaLeft", label: "Win/Cmd", width: "key-md" },
      { code: "AltLeft", label: "Alt", width: "key-md" },
      { code: "Space", label: "Space", width: "key-space" },
      { code: "AltRight", label: "Alt", width: "key-md" },
      { code: "MetaRight", label: "Win/Cmd", width: "key-md" },
      { code: "ControlRight", label: "Ctrl", width: "key-md" },
      { code: "ArrowLeft", label: "←" },
      { code: "ArrowUp", label: "↑" },
      { code: "ArrowDown", label: "↓" },
      { code: "ArrowRight", label: "→" },
    ],
  ];

  return (
    <div className="container keyboard-container" onClick={handleContainerClick}>
      {/* Hidden input to trap focus and disable Vim extensions */}
      <input
        ref={inputRef}
        type="text"
        className="hidden-input"
        autoComplete="off"
        autoFocus
      />
      
      <h1 className="keyboard-title">Keyboard Tester</h1>
      <div className="keyboard-status">
        Press keys on your physical keyboard to test them.
        <br />
        <small style={{ color: "#e74c3c" }}>
          * Browser shortcuts and extensions (like Vimium) are disabled to allow testing.
        </small>
      </div>

      <div className="keyboard-base">
        {rows.map((row, rowIndex) => (
          <div key={rowIndex} className="keyboard-row">
            {row.map((key) => (
              <div
                key={key.code}
                className={`${getKeyClass(key.code)} ${key.width || ""}`}
              >
                <span>{key.label}</span>
              </div>
            ))}
          </div>
        ))}
      </div>

      <button className="reset-btn" onClick={(e) => { e.stopPropagation(); resetTest(); }}>
        Reset
      </button>
    </div>
  );
};

export default KeyboardTest;
